import { memo, Fragment } from 'react';
import type { ReactNode } from 'react';
import Table from './table/Table';
import TableBody from './table/TableBody';
import TableCell from './table/TableCell';
import TableHead from './table/TableHead';
import TableRow from './table/TableRow';

const tags: {
    [key: string]:
        | ((
              element: Element,
              key: string | number,
              children: () => ReactNode
          ) => ReactNode)
        | undefined;
} = {
    p: (_, key, children) => <p key={key}>{children()}</p>,
    ul: (_, key, children) => <ul key={key}>{children()}</ul>,
    li: (_, key, children) => <li key={key}>{children()}</li>,
    u: (_, key, children) => <u key={key}>{children()}</u>,
    strong: (_, key, children) => <strong key={key}>{children()}</strong>,
    table: (_, key, children) => (
        <Table bordered borderedCells collapseBorder key={key}>
            {children()}
        </Table>
    ),
    thead: (_, key, children) => <TableHead key={key}>{children()}</TableHead>,
    tbody: (_, key, children) => <TableBody key={key}>{children()}</TableBody>,
    tr: (_, key, children) => <TableRow key={key}>{children()}</TableRow>,
    td: (_, key, children) => <TableCell key={key}>{children()}</TableCell>,
    '#text': ({ textContent }, key) => (
        <Fragment key={key}>{textContent}</Fragment>
    ),
    svg: ({ outerHTML }, key) => (
        // TODO: add class and provide a size for svg elements
        <span
            key={key}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: outerHTML }}
        />
    ),
};

const mapChildren = (children: NodeListOf<Node & ChildNode>): ReactNode => {
    return Array.from(children).map((node, key) => {
        const element = node as Element;
        const nodeName = element.nodeName.toLowerCase();
        const childrenFn = () => {
            return mapChildren(element.childNodes);
        };

        const presented = tags[nodeName];
        if (presented) {
            return presented(element, key, childrenFn);
        }

        return element.textContent;
    });
};

const parser = new DOMParser();

const Markup = ({ content }: { content: string }) => {
    const doc = parser.parseFromString(`<body>${content}</body>`, 'text/html');
    try {
        const result = mapChildren(doc.body.childNodes);
        return <>{result}</>;
    } catch (e) {
        /* eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any */
        return <>{(e as any).toString()}</>;
    }
};

export default memo(Markup);
